"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { buildMadeWithHref, type MadeWithSpot } from "@/lib/made-with";
import { cn } from "@/lib/utils";
import { useUploadSessionBridge } from "@/lib/upload-session-bridge";
interface MadeWithLineProps {
  spot: MadeWithSpot;
  className?: string;
}
const BRAND = "Guest.Gallery";

// «Guest.Gallery» as the wordmark writes it: body ink with the logo's orange
// full stop. Every locale's copy names the brand (tests/made-with).
function withWordmark(text: string): React.ReactNode {
  const index = text.indexOf(BRAND);
  if (index === -1) return text;
  return <>
      {text.slice(0, index)}
      <span className="text-[#3b332b]">
        Guest<span className="text-[#f9b144]">.</span>Gallery
      </span>
      {text.slice(index + BRAND.length)}
    </>;
}

// One quiet line at the end of the guest page (approval 316). Only GalleryEnd,
// the closed gallery and the guest-page footer render it; never the upload
// hero, the dialog, the prompt, the lightbox or the photo wall
// (tests/made-with). While the upload dialog is open or a file is still
// sending it stays in place but hidden and untappable, and it opens in a new
// tab without a referrer, so a tap never takes a guest away from an upload
// and the guest page's address never reaches the home page.
export default function MadeWithLine(t0) {
  const $ = _c(24);
  const {
    spot,
    className
  } = t0;
  const t = useTranslations("Upload.madeWith");
  const locale = useLocale();
  const isUploading = useUploadSessionBridge(_temp);
  let t1;
  if ($[0] !== locale || $[1] !== spot) {
    t1 = buildMadeWithHref(locale, spot);
    $[0] = locale;
    $[1] = spot;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = isUploading || undefined;
  const t3 = isUploading ? -1 : undefined;
  const t4 = isUploading && "invisible";
  let t5;
  if ($[3] !== className || $[4] !== t4) {
    t5 = cn("group inline-flex min-h-11 items-center gap-1.5 text-sm leading-5 text-[#71675d] transition-colors hover:text-[#3b332b] focus-visible:text-[#3b332b]", t4, className);
    $[3] = className;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t) {
    t6 = withWordmark(t("made"));
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <span>{t6}</span>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span aria-hidden="true">·</span>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t) {
    t9 = t("ask");
    $[11] = t;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t9) {
    t10 = <span className="underline decoration-[#71675d]/40 decoration-1 underline-offset-[3px] transition-colors group-hover:decoration-[#3b332b] group-focus-visible:decoration-[#3b332b]">{t9}</span>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ArrowUpRight className="h-3.5 w-3.5 shrink-0 translate-y-px" strokeWidth={1.75} aria-hidden="true" />;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== spot || $[17] !== t1 || $[18] !== t10 || $[19] !== t2 || $[20] !== t3 || $[21] !== t5 || $[22] !== t7) {
    t12 = <a href={t1} target="_blank" rel="noopener noreferrer" aria-hidden={t2} tabIndex={t3} data-made-with={spot} className={t5}>{t7}{t8}{t10}{t11}</a>;
    $[16] = spot;
    $[17] = t1;
    $[18] = t10;
    $[19] = t2;
    $[20] = t3;
    $[21] = t5;
    $[22] = t7;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}
function _temp(bridge) {
  return bridge.isDialogOpen || bridge.hasActiveUploads;
}